  import React, { useEffect, useState, useRef } from "react";
  import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
  import Footer from "../../structures/Footer";
  import "../../../assets/css/booking-success.css";

  export default function SuccessPage() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Support for payment gateways that redirect back and attach ID to query parameter
    const queryId = searchParams.get("id") || searchParams.get("transaction_id") || searchParams.get("invoice_number") || searchParams.get("order_id") || searchParams.get("merchantOrderId") || searchParams.get("bookingId");

    const API_BASE = process.env.REACT_APP_BACKEND_API || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

    // Ambil bookingData dari state atau localStorage
    const [bookingData, setBookingData] = useState(() => {
      if (state) return state;
      const saved = localStorage.getItem("bookingData");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Failed to parse saved booking data");
          return null;
        }
      }
      return null;
    });
    
    // Cegah 'Blink Error' saat memuat data dari parameter URL
    const [isLoadingData, setIsLoadingData] = useState(() => {
      const saved = localStorage.getItem("bookingData");
      return !state && !saved && !!queryId;
    });
    const [showConfetti, setShowConfetti] = useState(false);
    const [invoiceUrl, setInvoiceUrl] = useState(state?.invoice?.url || null);
    const pollingRef = useRef(null);
    const hadFetchError = useRef(false);

    // SAFE ACCESS
    const bookingId = bookingData?.bookingId || queryId || null;

    // 2. FETCH LATEST DATA FROM BACKEND
    useEffect(() => {
      if (!bookingId) return;

      const fetchFullData = async () => {
        setIsLoadingData(true);
        try {
          const res = await fetch(`${API_BASE}/api/bookings/${bookingId}`, {
            headers: { "ngrok-skip-browser-warning": "true" }
          });
          const json = await res.json();
          if (json.success && json.data) {
            const freshData = Array.isArray(json.data) ? json.data[0] : json.data;
            
// SECURITY CHECK: Jika masih pending, lempar ke halaman pending
             if (freshData.status === "PENDING_PAYMENT") {
               const isMantos = freshData?.experience?.id?.startsWith('mantos-');
               navigate(`${isMantos ? '/mantos/booking/pending' : '/booking/pending'}?bookingId=${bookingId}`, { state: freshData, replace: true });
               return;
             }

            setBookingData(freshData);
            if (freshData.invoice?.url) {
              setInvoiceUrl(freshData.invoice.url);
            }
          }
        } catch (err) {
          console.error("Failed to fetch fresh booking data", err);
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchFullData();
    }, [bookingId, API_BASE]);

    /* =========================
      1️⃣ FETCH INVOICE
      ========================= */
    useEffect(() => {
        if (!bookingId) return;

        // If navigation `state` already includes an invoice URL, use it immediately
        if (state?.invoice?.url) {
          setInvoiceUrl(state.invoice.url);
          return;
        }

        let retryCount = 0;

        const fetchInvoice = async () => {
          try {
            // avoid conditional requests / 304 by adding a cache-busting query
            const url = `${API_BASE}/api/bookings/${bookingId}?_=${Date.now()}`;
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) return false;
            const json = await res.json();
            if (json.success && json.data) {
              const bookingObj = Array.isArray(json.data) ? json.data[0] : json.data;
              if (bookingObj && bookingObj.invoice?.url) {
                setInvoiceUrl(bookingObj.invoice.url);
                return true;
              }
            }
          } catch (err) {
            console.error("Failed to fetch invoice", err);
            // stop further polling on persistent fetch errors (CORS, network)
            hadFetchError.current = true;
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
          return false;
        };

        // Try immediately, then poll every 3s until found or maxRetry
        (async () => {
          const foundNow = await fetchInvoice();
          if (foundNow) return;

          pollingRef.current = setInterval(async () => {
            // if we already had a persistent fetch error, stop polling
            if (hadFetchError.current) return;

            retryCount++;
            const found = await fetchInvoice();

            if (found || retryCount >= 6) {
              if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
              }
            }
          }, 3000); // cek tiap 3 detik
        })();

        return () => {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        };
    }, [bookingId]);


    /* =========================
      2️⃣ CONFETTI + SCROLL
      ========================= */
    useEffect(() => {
      setShowConfetti(true);

      // Pastikan animasi hanya berjalan ketika kita sedang menampilkan konten utama
      if (isLoadingData || !bookingData) return;

      let observer = null;
      // Beri sedikit jeda agar DOM benar-benar ter-render
      const timeoutId = setTimeout(() => {
        const elements = document.querySelectorAll(".animate-on-scroll");
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add("animate");
              }
            });
          },
          { threshold: 0.1 }
        );

        elements.forEach((el) => observer.observe(el));
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        if (observer) observer.disconnect();
      };
    }, [isLoadingData, bookingData]);

    /* =========================
      3️⃣ GUARD SETELAH HOOKS
      ========================= */
    if (isLoadingData) {
      return (
        <div className="success-page-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Retrieving your booking details...</p>
          </div>
        </div>
      );
    }

    if (!bookingData) {
      return (
        <div className="success-page-container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Booking Not Found</h2>
            <p>Data booking tidak ditemukan. Silakan lakukan booking ulang atau periksa pembayaran Anda.</p>
            {queryId ? (
              <p className="text-sm mt-4 text-gray-500">
                Booking ID from URL: {queryId} but could not automatically retrieve details.
              </p>
            ) : null}
            <button onClick={() => navigate("/mantos")}> 
              Back to Mantos Experiences
            </button>
          </div>
        </div>
      );
    }

    /* =========================
      4️⃣ DESTRUCTURE DATA
      ========================= */
    const {
      name,
      email,
      phone,
      pax,
      date,
      time,
      experience,
      experiences,
      totalPrice,
      originalPrice,
      discount,
      status,
      withDrone,
      promoCode,
    } = bookingData;

    // Helper to determine if this is a mantos booking
    const isMantosExperience = experience?.id?.startsWith('mantos-');

    /* =========================
      5️⃣ HELPERS
      ========================= */
    const getExperienceDetails = (expVal) => {
      // 1. Jika berbentuk objek penuh bawaan state/localStorage
      if (expVal && typeof expVal === 'object' && expVal.name && expVal.icon) {
        return expVal;
      }
      
      const key = typeof expVal === 'string' ? expVal.toLowerCase() : String(expVal?.name || expVal?.id || '').toLowerCase();
      
      // Mantos experiences
      if (key === 'mantos-paint-stoneware') return { name: "Pottery Paint Stoneware", icon: "🎨🏺", duration: "1 HOUR", price: 450000 };
      if (key === 'mantos-stoneware') return { name: "Pottery Stoneware", icon: "🏺", duration: "1 HOUR", price: 250000 };
      if (key === 'mantos-painting-earthen') return { name: "Painting Earthen", icon: "🎨", duration: "1 HOUR", price: 150000 };
      if (key === 'mantos-stoneware-painting-earthen') return { name: "Pottery Stoneware Painting Earthen", icon: "🏺🎨", duration: "1 HOUR", price: 400000 };
      if (key === 'mantos-pinch') return { name: "Pottery Pinch", icon: "🏺", duration: "1 HOUR", price: 200000 };
      
      // Match by experience ID (exact matches first)
      if (key === 'combo-stoneware') return { name: "Paint & Pottery (Stoneware)", icon: "🎭", duration: "3 HOURS", price: 500000 };
      if (key === 'pottery-stoneware') return { name: "Pottery (Stoneware)", icon: "🏺", duration: "2 HOURS", price: 300000 };
      if (key === 'paint-on-pottery') return { name: "Paint on Pottery (Earthenware)", icon: "🎨", duration: "2 HOURS", price: 200000 };
      if (key === 'combo' || key.includes("pottery-paint") || key.includes("pottery & painting")) return { name: "Pottery & Painting (Earthenware)", icon: "🎭", duration: "3 HOURS", price: 350000 };
      if (key === 'pottery-stoneware-painting-earthenware') return { name: "Pottery (Stoneware) & Painting (Earthenware)", icon: "🏺🎨", duration: "3 HOURS", price: 450000 };
      if (key.includes("cycle") || key.includes("bicycle")) {
        if (key.includes("drone") || key.includes("video")) return { name: "Bicycle Rental (Drone Content)", icon: "🚴", duration: "1-2.5 HOURS", price: 250000 };
        return { name: "Bicycle Rental (Basic)", icon: "🚴", duration: "1-2.5 HOURS", price: 175000 };
      }
      if (key === 'pottery' || key.includes("pottery (earth")) return { name: "Pottery (Earthenware)", icon: "🏺", duration: "2 HOURS", price: 200000 };
      if (key.includes("sail")) return { name: "Sailing Trip", icon: "⛵", duration: "20-30 MINS", price: 50000 };
      if (key.includes("kayak")) return { name: "Kayak / Canoeing", icon: "🛶", duration: "30 MINS", price: 275000 };
      if (key.includes("wed") || key.includes("prewed") || key.includes("photoshoot")) return { name: "Photoshoot Package", icon: "📸", duration: "3 HOURS", price: 1500000 };
      
// 2. Fallback by price per unit
       const pricePerUnit = totalPrice / (pax || 1);
       if (pricePerUnit === 450000) return { name: "Pottery Paint Stoneware", icon: "🎨🏺", duration: "1 HOUR", price: 450000 };
       if (pricePerUnit === 250000) return { name: "Pottery Stoneware", icon: "🏺", duration: "1 HOUR", price: 250000 };
       if (pricePerUnit === 150000) return { name: "Painting Earthen", icon: "🎨", duration: "1 HOUR", price: 150000 };
       if (pricePerUnit === 400000) return { name: "Pottery Stoneware Painting Earthen", icon: "🏺🎨", duration: "1 HOUR", price: 400000 };
       if (pricePerUnit === 200000) return { name: "Pottery Pinch", icon: "🏺", duration: "1 HOUR", price: 200000 };
       if (pricePerUnit === 175000) return { name: "Bicycle Rental (Basic)", icon: "🚴", duration: "1-2.5 HOURS", price: 175000 };
       if (pricePerUnit === 250000) return { name: "Bicycle Rental (Drone Content)", icon: "🚴", duration: "1-2.5 HOURS", price: 250000 };
       if (pricePerUnit === 500000) return { name: "Paint & Pottery (Stoneware)", icon: "🎭", duration: "3 HOURS", price: 500000 };
       if (pricePerUnit === 350000) return { name: "Pottery & Painting (Earthenware)", icon: "🎭", duration: "3 HOURS", price: 350000 };
       if (pricePerUnit === 300000) return { name: "Pottery (Stoneware)", icon: "🏺", duration: "2 HOURS", price: 300000 };
       if (pricePerUnit === 200000) return { name: "Creative Workshop", icon: "🎨", duration: "2 HOURS", price: 200000 };
       if (pricePerUnit === 50000) return { name: "Sailing Trip", icon: "⛵", duration: "20-30 MINS", price: 50000 };
       if (pricePerUnit === 1500000) return { name: "Photoshoot Package", icon: "📸", duration: "3 HOURS", price: 1500000 };

      return { 
        name: bookingData?.experienceName || "Premium Experience", 
        icon: "🎫", 
        duration: "1-2 HOURS", 
        price: pricePerUnit 
      };
    };

    const experienceList = (experiences && experiences.length > 0) ? experiences : [experience];
    const allExpDetails = experienceList.map(exp => getExperienceDetails(exp));

    const formatDate = (d) =>
      new Date(d).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

    const formatTime = (t) => {
      const [h, m] = t.split(":");
      const hour = parseInt(h);
      return `${hour % 12 || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
    };

    /* =========================
      6️⃣ DOWNLOAD INVOICE
      ========================= */
    const handleDownloadInvoice = async () => {
      // If we already have the invoice URL, open it
      if (invoiceUrl) {
        window.open(invoiceUrl, "_blank");
        return;
      }

      // Otherwise, try to fetch the booking to obtain the invoice URL immediately
      try {
        const fetchUrl = `${API_BASE}/api/bookings/${bookingId}`;
        console.debug("Fetching booking for invoice:", fetchUrl);
        // append cache-busting query param to avoid 304 responses without triggering CORS preflight
        const res = await fetch(fetchUrl + `?_=${Date.now()}`, { cache: 'no-store' });

        if (!res.ok) {
          console.error("Booking fetch failed status", res.status, res.statusText);
          alert(`Gagal mengambil booking (status ${res.status}). Cek server.`);
          return;
        }

        const json = await res.json();
        console.debug("Booking fetch response:", json);

          if (json.success && json.data) {
            const bookingObj = Array.isArray(json.data) ? json.data[0] : json.data;
            const invoiceObj = bookingObj?.invoice;
            if (invoiceObj && invoiceObj.url) {
              const url = invoiceObj.url;
              setInvoiceUrl(url);
              window.open(url, "_blank");
              return;
            }

          // API responded but invoice not yet attached
          console.warn('Invoice not present in booking response yet', json.data[0]);
          alert("Invoice belum tersedia pada server. Silakan coba beberapa saat lagi.");
          return;
        }

        // API returned but no useful data
        console.warn('Booking API returned unexpected payload', json);
        alert("Booking tidak ditemukan atau respons server tidak valid.");
      } catch (err) {
        console.error("Failed to fetch invoice on demand", err);
        alert("Gagal mengambil invoice. Coba beberapa saat lagi.");
      }
    };



    /* =========================
      7️⃣ RENDER
      ========================= */

    return (
      <div className="success-page-container">
        {/* Confetti Animation - Only show if PAID */}
        {showConfetti && status === 'PAID' && (
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div 
                key={i} 
                className="confetti" 
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  backgroundColor: ['#8B7355', '#6B5B47', '#E8DCC8', '#fff'][Math.floor(Math.random() * 4)]
                }}
              />
            ))}
          </div>
        )}

        {/* Success/Pending Header */}
        <section className="success-header animate-on-scroll">
          <div className="success-icon-wrapper">
            <div className={`success-icon ${status === 'PENDING_PAYMENT' ? 'pending' : ''}`}>
              {status === 'PENDING_PAYMENT' ? '!' : '✓'}
            </div>
            <div className="success-ring"></div>
          </div>
          <h1 className="success-title">
            {status === 'PENDING_PAYMENT' ? 'Payment Pending' : 'Booking Confirmed!'}
          </h1>
          <p className="success-subtitle">
            {status === 'PENDING_PAYMENT' 
              ? 'Your booking is reserved, but we are waiting for your payment' 
              : 'Your experience at À L\'AISE has been successfully reserved'}
          </p>
          <div className="success-ornament">◆ ◆ ◆</div>
        </section>

        {/* Booking Details */}
        <section className="success-content animate-on-scroll">
          <div className="success-content-wrapper">
            
            {/* Main Confirmation Card */}
            <div className="confirmation-card">
              <div className="card-header">
                <h2>Booking Confirmation</h2>
                <div className="booking-id">
                  <span className="id-label">Booking ID</span>
                  <span className="id-value">{bookingId}</span>
                </div>
              </div>

              {/* Guest Information */}
              <div className="info-section">
                <h3 className="section-title">Guest Information</h3>
                <div className="info-grid">
                  <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                    <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>👤</span>
                    <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                      <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Full Name</span>
                      <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{name}</span>
                    </div>
                  </div>
                  <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                    <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>📧</span>
                    <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                      <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Email</span>
                      <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{email}</span>
                    </div>
                  </div>
                  <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                    <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>📱</span>
                    <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                      <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Phone</span>
                      <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{phone}</span>
                    </div>
                  </div>
                  <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                    <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>👥</span>
                    <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                      <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Number of Guests</span>
                      <span className="info-value" style={{ fontSize: '16px', color: '#111', fontFamily: '"Playfair Display", serif' }}>{pax} {pax > 1 ? 'people' : 'person'}</span>
                    </div>
                  </div>
                  {promoCode && (
                    <div className="info-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                      <span className="info-icon" style={{ fontSize: '24px', minWidth: '30px', textAlign: 'center' }}>◎</span>
                      <div className="info-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                        <span className="info-label" style={{ fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#999', fontWeight: '600' }}>Promo Code</span>
                        <span className="info-value" style={{ fontSize: '16px', color: '#c9a96e', fontWeight: 'bold' }}>{promoCode.toUpperCase()}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Experience Details */}
              <div className="info-section">
                <h3 className="section-title">Experience Details</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {allExpDetails.map((details, idx) => (
                    <div key={idx} className="experience-summary" style={{ marginBottom: idx === allExpDetails.length - 1 ? 0 : '10px' }}>
                      <div className="experience-header">
                        <span className="experience-icon">{details.icon}</span>
                        <div>
                          <h4>{details.name}</h4>
                          <p className="experience-duration">{details.duration}</p>
                        </div>
                      </div>
                      {idx === 0 && (
                        <div className="experience-schedule">
                          <div className="schedule-item">
                            <span className="schedule-icon">📅</span>
                            <div>
                              <span className="schedule-label">Date</span>
                              <span className="schedule-value">{formatDate(date)}</span>
                            </div>
                          </div>
                          <div className="schedule-item">
                            <span className="schedule-icon">🕐</span>
                            <div>
                              <span className="schedule-label">Time</span>
                              <span className="schedule-value">{formatTime(time)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Summary */}
              <div className="info-section payment-section">
                <h3 className="section-title">Payment Summary</h3>
                <div className="payment-details">
                  {allExpDetails.map((details, idx) => (
                    <div key={idx} className="payment-row">
                      <span>{details.name} (x{pax})</span>
                      <span>IDR {(((originalPrice || totalPrice) - (withDrone ? 300000 : 0)) / allExpDetails.length).toLocaleString()}</span>
                    </div>
                  ))}
                  {withDrone && (
                    <div className="payment-row">
                      <span>Drone Content Add-on</span>
                      <span>IDR 300,000</span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="payment-row">
                      <span>🎁 Promo Discount {promoCode ? `(${promoCode.toUpperCase()})` : ''}</span>
                      <span style={{ color: '#dc2626', fontWeight: '600' }}>- IDR {discount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="payment-divider"></div>
                  <div className="payment-row total">
                    <span>Total Amount</span>
                    <span>IDR {totalPrice.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="payment-status">
                  <span className="status-badge pending">
                    <span className="status-dot"></span>
                    {status === 'PENDING_PAYMENT' ? 'Awaiting Payment' : status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <section className="success-actions animate-on-scroll">
          {status === 'PENDING_PAYMENT' && (
<button 
             className="action-btn primary"
             onClick={() => navigate(isMantosExperience ? "/mantos/booking/payment" : "/booking/payment")}
           >
             <span>Complete Payment</span>
             <span className="btn-icon">💳</span>
           </button>
          )}
          
          <button 
            className={`action-btn ${status === 'PAID' ? 'primary' : 'secondary'}`}
            onClick={handleDownloadInvoice}
          >
            <span>{status === 'PAID' ? 'Download Invoice' : 'View Booking Details (PDF)'}</span>
            <span className="btn-icon">🖨️</span>
          </button>
          
          <button 
            className="action-btn secondary"
            onClick={() => navigate("/")}
          >
            <span>Back to Home</span>
            <span className="btn-icon">🏠</span>
          </button>
        </section>

        {/* Thank You Message */}
        <section className="thank-you-section animate-on-scroll">
          <div className="thank-you-content">
            <h3>Thank You for Choosing À L'AISE</h3>
            <p>
              We're honored to be part of your journey. Our team is preparing to welcome you 
              and ensure every moment of your experience is memorable. See you soon by the lake!
            </p>
            <div className="signature">
              <span className="signature-line"></span>
              <span className="signature-text">The À L'AISE Team</span>
            </div>
          </div>
        </section>
      </div>
    );
  }